import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import GroupAdd from '@mui/icons-material/GroupAdd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useHasPermission } from '@/components/PermissionGate';
import { CommandResultSnackbar } from '@/components/CommandResultSnackbar';
import { DialogValidationErrors } from '@/components/forms/DialogValidationErrors';
import { ValidationError, toFormErrors } from '@/lib/validationErrors';
import { identityKeys, useAssignableRoles } from '@/hooks/identity/useIdentity';
import { userListService } from '@/services/identity/userListService';
import { RoleCheckboxList } from '@/core/RoleCheckboxList';
import type { ListViewExtraProps } from '@/config/listViewExtras';

/** ABP's permission for changing a user's roles — the server's gate on add-roles. */
export const ASSIGN_ROLES_PERMISSION = 'AbpIdentity.Users.Update.ManageRoles';

interface AssignUserRolesDialogProps {
  open: boolean;
  userIds: string[];
  onClose: () => void;
  /** Called with the number of users the roles were added to. */
  onAssigned: (userCount: number) => void;
}

function AssignUserRolesDialog({ open, userIds, onClose, onAssigned }: AssignUserRolesDialogProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data: roles } = useAssignableRoles();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [apiValidationErrors, setApiValidationErrors] = useState<ValidationError[]>([]);

  const reset = () => {
    setSelected(new Set());
    setApiValidationErrors([]);
  };

  const mutation = useMutation({
    mutationFn: () => userListService.addRoles(userIds, [...selected]),
    onSuccess: () => {
      // An open user-edit page must show the new roles, not its cached set.
      void queryClient.invalidateQueries({ queryKey: identityKeys.users.all });
      reset();
      onAssigned(userIds.length);
    },
    onError: (error: unknown) => setApiValidationErrors(
      toFormErrors(error, t('common.genericError', { defaultValue: 'An error occurred' })),
    ),
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const toggleRole = (roleName: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(roleName)) next.delete(roleName);
      else next.add(roleName);
      return next;
    });
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('identity.assignRoles')}</DialogTitle>
      <DialogContent>
        <DialogValidationErrors errors={apiValidationErrors} onClear={() => setApiValidationErrors([])} />
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('identity.assignRolesIntro', { count: userIds.length })}
        </Typography>
        <RoleCheckboxList roles={roles} selected={selected} onToggle={toggleRole} />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          disabled={selected.size === 0 || mutation.isPending}
          onClick={() => mutation.mutate()}
        >
          {t('identity.assignRoles')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Toolbar action on the Users grid: add roles to every selected user. Roles a user already has
 * are kept — the server adds, never replaces — and an operator who is not an admin can hand out
 * only roles they hold (ABP's own bound, applied per user on the server).
 */
export function AssignUserRolesButton({ selectedIds, onActionCompleted }: ListViewExtraProps) {
  const { t } = useTranslation();
  const allowed = useHasPermission(ASSIGN_ROLES_PERMISSION);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [assignedCount, setAssignedCount] = useState<number | null>(null);

  if (!allowed) return null;

  const handleAssigned = (userCount: number) => {
    setDialogOpen(false);
    setAssignedCount(userCount);
    onActionCompleted?.();
  };

  return (
    <>
      <Button
        startIcon={<GroupAdd />}
        disabled={selectedIds.length === 0}
        onClick={() => setDialogOpen(true)}
        sx={{ textTransform: 'none', color: 'text.secondary' }}
      >
        {t('identity.assignRoles')}
        {selectedIds.length > 0 ? ` (${selectedIds.length})` : ''}
      </Button>

      <AssignUserRolesDialog
        open={dialogOpen}
        userIds={selectedIds}
        onClose={() => setDialogOpen(false)}
        onAssigned={handleAssigned}
      />

      <CommandResultSnackbar
        result={assignedCount !== null
          ? { severity: 'success', message: t('identity.rolesAssigned', { count: assignedCount }) }
          : null}
        onClose={() => setAssignedCount(null)}
      />
    </>
  );
}
